import { fetchListaDocumentos } from '@/services/Documentos/api/ListaDocumentosApi';
import type { Documento, DocumentosData, FiltrosDocumentos } from '@/services/Documentos/types';
import type { FiltrosGestaoImpostos, TotaisImpostosTradicionais } from '../types';

// Tamanho de página grande para reduzir o número de chamadas — ver risco de
// performance registrado em design.md (seção "Risks / Trade-offs").
const TAMANHO_PAGINA = 100;

// Trava de segurança: nenhum período/empresa real deveria precisar de mais
// que isso; existe só para nunca entrar em loop infinito se a API se
// comportar de forma inesperada (ex.: totalItens sempre maior que o buscado).
const LIMITE_PAGINAS_POR_DIRECAO = 200;

function parseValorApi(valor: string | undefined | null): number {
  if (!valor) return 0;
  const num = Number(valor);
  return Number.isFinite(num) ? num : 0;
}

function somarGrupo(
  acc: { icms: number; ipi: number; pis: number; cofins: number; qtd: number },
  itens: Documento[] | undefined
): void {
  if (!itens) return;
  for (const doc of itens) {
    acc.icms += parseValorApi(doc.vICMS);
    acc.ipi += parseValorApi(doc.vIPI);
    acc.pis += parseValorApi(doc.vPIS);
    acc.cofins += parseValorApi(doc.vCOFINS);
    acc.qtd += 1;
  }
}

function itensDaPagina(documentos: DocumentosData['documentos']): number {
  return Object.values(documentos).reduce((total, grupo) => total + (grupo?.itens.length ?? 0), 0);
}

/**
 * Soma ICMS/IPI/PIS/COFINS de todos os documentos (entrada + saída, todos os
 * modelos) do período/empresa filtrados, paginando `Documento/ListaDocumentos`.
 *
 * Correção registrada em design.md: `Documento/ListaDocumentosItens` foi
 * cogitado antes, mas exige `chave_acesso` de um documento por chamada —
 * inviável para um período inteiro. `ListaDocumentos` pagina por documento
 * (cabeçalho), o que é tratável.
 */
export async function fetchTotaisImpostosTradicionais(
  filtros: FiltrosGestaoImpostos
): Promise<TotaisImpostosTradicionais> {
  const acc = { icms: 0, ipi: 0, pis: 0, cofins: 0, qtd: 0 };

  for (const entradaSaida of ['0', '1'] as const) {
    let pagina = 1;
    for (; pagina <= LIMITE_PAGINAS_POR_DIRECAO; pagina++) {
      const filtrosPagina: FiltrosDocumentos = {
        empresaId: filtros.empresaId,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        entradaSaida,
        paginaNumero: pagina,
        paginaQuantidadePor: TAMANHO_PAGINA,
      };

      const resposta = await fetchListaDocumentos(filtrosPagina);
      const documentos = resposta.data?.documentos ?? {};

      for (const grupo of Object.values(documentos)) {
        somarGrupo(acc, grupo?.itens);
      }

      if (itensDaPagina(documentos) === 0) break;
    }
  }

  return {
    icms: acc.icms,
    ipi: acc.ipi,
    pis: acc.pis,
    cofins: acc.cofins,
    totalDocumentosSomados: acc.qtd,
  };
}
